import { mapCatalogToCanonical } from "./bodymap.js";
import {
  CATALOG_MUSCLE_SLUGS,
  type CatalogMuscleSlug,
} from "./muscleCatalog.js";

// Single aggregator for volume-by-muscle across the whole stack.
// Replaces the divergent loops that previously lived in
// sessionResult.ts (full primary + half secondary, double-counting on
// multi-primary lifts) and apps/mobile/src/logger/totalsMath.ts
// (`primary[0]` only, sub-counting). Phase 4 of the muscle-breakdown
// rebuild — see next/muscle-breakdown.md.
//
// Contract (slice #4): the catalog row owns the recruitment shape via
//   Exercise.muscleContrib: Partial<Record<CatalogMuscleSlug, [0, 1]>>
// stored raw. The volume math normalises per exercise (#529):
//   volumeByMuscle[slug] += setVolumeKg × contrib[slug] / Σ contrib
// applied identically on every consumer (Logger live totals, post-
// session card, share PNG, Progress) so the four surfaces show the
// SAME number for the same session to the gram. Vocabulary is the
// frozen 19-slug catalog enum (muscleCatalog.ts) — NOT the bodymap
// MUSCLE_SLUGS (~15 anatomical regions used for the pain picker).
// The bodymap bridge survives ONLY at the paint-map layer
// (volumeBySlugToHeatmap), never inside the volume math.
//
// Back-compat: catalog rows without `muscleContrib` (a row Phase 3 missed
// or a future custom user row) fall back to the legacy `primary[0] @ 1.0`
// rule, mapped to a canonical catalog slug. Hand the helper a row with
// neither field and that exercise simply doesn't contribute — no crash,
// no fake muscle.
//
// ⛔ NOT cardio (corrected 2026-09-14). Cardio rows DO carry both
// `muscleContrib` and `primary` — all 17 of them. They are absent from
// this map for an entirely different reason, one line of the aggregator
// below: a cardio bout has `volumeKg === 0` (setMetrics.ts, Antenore
// 2026-07-22 «lascia il cardio fuori») and the aggregator gates on
// `volumeKg <= 0`, so it is skipped before contribs are
// ever resolved.
// ⛔ And that gate is CORRECT — do not "fix" it. Antenore 2026-09-14:
// cardio gets its OWN bodymap (muscleContrib × minutes, legend "work
// time"), never kg on this one. Mixing the two would put two different
// magnitudes on one axis, which §16.3 forbids. CD-3 builds the second
// map; it does not touch this file.

export type VolumeByMuscleMap = Partial<Record<CatalogMuscleSlug, number>>;

export type VolumeBySetInput = {
  exerciseId: string;
  /** Already-rolled-up kg×reps×sides for the set (or for a whole
   *  entry, depending on caller granularity — the helper sums what
   *  it's handed). Failed sets and zero-volume sets should be
   *  filtered out before calling. */
  volumeKg: number;
};

// Normalise legacy slugs to canonical catalog vocab. Covers:
//   - the two pre-Phase-3 catalog typos (chest, upper-back),
//   - the bodymap MUSCLE_SLUGS vocabulary stored on SessionResult rows
//     written before slice #4 — those rows had bodymap keys (chest,
//     abs, gluteal, hamstring, front-deltoids, ...) that the renderer
//     now normalises into catalog vocab so legacy rows display with
//     the same labels as fresh rows.
// Returns null when the input slug has no canonical catalog equivalent
// (bodymap-only regions: head, neck, tibialis). Those legacy entries
// get dropped from the rendered map — they were never trained anyway
// in any realistic session.
const CATALOG_SLUG_SET: ReadonlySet<string> = new Set(CATALOG_MUSCLE_SLUGS);
const LEGACY_TO_CATALOG: Readonly<Record<string, CatalogMuscleSlug>> = {
  // Pre-Phase-3 catalog typos
  chest: "pectoralis",
  "upper-back": "rhomboids",
  // Pre-slice-#4 bodymap vocabulary (stored SessionResult rows)
  abs: "core",
  "back-deltoids": "rear-delts",
  forearm: "forearms",
  "front-deltoids": "anterior-delts",
  gluteal: "glutes",
  hamstring: "hamstrings",
  "lower-back": "erectors",
  trapezius: "traps",
  // head, neck, tibialis: no catalog counterpart, drop.
};

export const toCatalogSlug = (raw: string): CatalogMuscleSlug | null => {
  if (CATALOG_SLUG_SET.has(raw)) return raw as CatalogMuscleSlug;
  const remap = LEGACY_TO_CATALOG[raw];
  return remap ?? null;
};

// Coerce a stored volume map (possibly carrying legacy bodymap keys
// from pre-slice-#4 rows) into a canonical catalog-vocab map.
// Renderers call this before iterating so legacy rows display with the
// same labels as fresh rows. Idempotent on already-canonical input.
export const normalizeVolumeByMuscle = (
  raw: Partial<Record<string, number>> | undefined | null,
): VolumeByMuscleMap => {
  const out: VolumeByMuscleMap = {};
  if (!raw) return out;
  for (const [k, v] of Object.entries(raw)) {
    if (v == null || !Number.isFinite(v) || v <= 0) continue;
    const canonical = toCatalogSlug(k);
    if (canonical === null) continue;
    out[canonical] = (out[canonical] ?? 0) + v;
  }
  return out;
};

// Catalog-row shape the aggregator reads. Loose by design: any object
// carrying `muscleContrib` and/or `primary` passes. SessionResult, the
// Logger totals path, and the share-card builder each supply their
// own narrower lookup shape (often a Pick<Exercise, ...>), and the
// readonly-vs-mutable variance on `primary` between consumers stays
// here rather than forcing each caller to widen.
export type AggregatorExerciseRow = {
  muscleContrib?: Partial<Record<string, number>>;
  primary?: readonly string[];
};

// Core aggregator. `inputs` is a flat list of {slug, kg}; the caller
// resolves volume per its own kinetics (weighted vs bodyweight vs
// cardio) and hands the helper a pre-rolled number. `exerciseBySlug`
// maps catalog slug → Exercise row (the helper reads `muscleContrib`
// or, on absence, falls back to `primary[0]`). Returns a sparse map.
export const aggregateVolumeByMuscle = (
  inputs: ReadonlyArray<VolumeBySetInput>,
  exerciseBySlug: ReadonlyMap<string, AggregatorExerciseRow>,
): VolumeByMuscleMap => {
  const out: VolumeByMuscleMap = {};
  for (const inp of inputs) {
    if (!Number.isFinite(inp.volumeKg) || inp.volumeKg <= 0) continue;
    const ex = exerciseBySlug.get(inp.exerciseId);
    if (!ex) continue;
    const contribs = resolveContribs(ex);
    if (contribs.length === 0) continue;
    // Tonnage is split, not multiplied: the weights sum to 1 per exercise
    // (#529), so 1000 kg stays 1000 kg across muscles. Hard-set credit
    // (resolveExerciseMuscleCredits) does not read this split.
    const total = contribs.reduce((a, [, w]) => a + w, 0);
    if (!(total > 0)) continue;
    for (const [slug, weight] of contribs) {
      out[slug] = (out[slug] ?? 0) + (inp.volumeKg * weight) / total;
    }
  }
  return out;
};

// Resolve an Exercise to a list of [slug, weight] contributions. Prefers
// the explicit `muscleContrib` map; falls back to `primary[0]` at 1.0
// for rows that pre-date the Phase 3 backfill.
// ⛔ This used to claim cardio rows "surface no primary either, so they
// contribute nothing — correct". Right conclusion, wrong reason, and the
// reason is what misleads: cardio rows have BOTH fields. See the header.
const resolveContribs = (
  ex: AggregatorExerciseRow,
): Array<[CatalogMuscleSlug, number]> => {
  if (ex.muscleContrib && Object.keys(ex.muscleContrib).length > 0) {
    const out: Array<[CatalogMuscleSlug, number]> = [];
    for (const [slug, weight] of Object.entries(ex.muscleContrib)) {
      const canonical = toCatalogSlug(slug);
      if (canonical === null) continue;
      if (typeof weight !== "number" || !Number.isFinite(weight)) continue;
      if (weight <= 0) continue;
      out.push([canonical, weight]);
    }
    return out;
  }
  const fallback = ex.primary?.[0];
  if (!fallback) return [];
  const canonical = toCatalogSlug(fallback);
  if (canonical === null) return [];
  return [[canonical, 1]];
};

// Index a catalog by slug — the one place that builds the `slug → row` lookup
// every muscle/volume consumer needs, so callers stop hand-rolling
// `new Map(catalog.map((e) => [e.slug, e]))` (a duplication tracked in
// next/consolidation.md). Generic over the row shape so a Pick<Exercise, …>
// or the full Exercise both index cleanly.
export const catalogBySlug = <T extends { slug: string }>(
  catalog: ReadonlyArray<T>,
): Map<string, T> => new Map(catalog.map((e) => [e.slug, e]));

// The SET of catalog muscles an exercise trains — the same recruitment
// resolution the volume math runs (muscleContrib keys > 0, else the
// primary[0] fallback), stripped of the weights. Callers that only need
// the muscle IDENTITY (the muscle-adjacency check, catalog filters)
// reuse this so "which muscles" is answered identically on every surface
// without fabricating a kg just to read the keys back. Empty for a row
// the aggregator can't resolve (cardio, untagged custom rows).
export const resolveExerciseMuscles = (
  ex: AggregatorExerciseRow,
): CatalogMuscleSlug[] => resolveContribs(ex).map(([slug]) => slug);

// The raw recruitment weight per muscle, unrounded: what the gap proposal
// ranks on, where the hard-set credit below ties every indirect muscle.
export const resolveExerciseMuscleWeights = (
  ex: AggregatorExerciseRow,
): Array<[CatalogMuscleSlug, number]> => resolveContribs(ex);

// The muscles an exercise SUBSTANTIALLY trains (contribution ≥ minContrib),
// same recruitment resolution as the volume math but thresholded. Used to
// attribute a hard set to its meaningful movers only (AP hard-sets-by-muscle),
// so incidental involvement (lats in a deadlift, quads in a pull-through)
// doesn't count as a hard set for that muscle. The primary[0] fallback resolves
// at weight 1.0, so untagged rows still attribute to their primary.
export const resolveExerciseMusclesAbove = (
  ex: AggregatorExerciseRow,
  minContrib: number,
): CatalogMuscleSlug[] =>
  resolveContribs(ex)
    .filter(([, weight]) => weight >= minContrib)
    .map(([slug]) => slug);

// The contribution at/above which a muscle counts as SUBSTANTIALLY trained, for
// the sites that only classify (target strip visibility, muscle-on-the-day,
// simulated frequency) via resolveExerciseMusclesAbove. Not the hard-set credit.
// They stay on contribution on purpose: they say «it works that day», not what a set is worth.
export const SUBSTANTIAL_MUSCLE_CONTRIB_MIN = 0.5;

/** Most hard-set credit a muscle the exercise does not list as primary can earn
 *  (#538, Antenore 02/11): fractional set counting, Pelland et al. 2024. */
export const INDIRECT_HARD_SET_CREDIT_MAX = 0.5;

// #538: hard-set credit per muscle, the same for every athlete. A muscle in the
// row's `primary` list is DIRECT and earns a full set (1), listed in
// muscleContrib or not; any other muscle the row trains is INDIRECT and earns
// its contribution, capped at INDIRECT_HARD_SET_CREDIT_MAX. The RP bands
// (MEV/MAV/MRV) count direct sets, so this is the number they compare against.
// A row with no `primary` (a custom one) takes its heaviest muscles as direct.
// One entry per catalog slug: legacy keys that collapse keep the highest credit.
export const resolveExerciseMuscleCredits = (
  ex: AggregatorExerciseRow,
): Array<[CatalogMuscleSlug, number]> => {
  const contribs = resolveContribs(ex);
  const primary = (ex.primary ?? []).map(toCatalogSlug).filter((s) => s !== null);
  const top = Math.max(0, ...contribs.map(([, w]) => w));
  const direct = new Set(
    primary.length > 0 ? primary : contribs.filter(([, w]) => w === top).map(([s]) => s),
  );
  const out = new Map<CatalogMuscleSlug, number>();
  for (const [slug, weight] of contribs) {
    const credit = direct.has(slug) ? 1 : Math.min(weight, INDIRECT_HARD_SET_CREDIT_MAX);
    out.set(slug, Math.max(out.get(slug) ?? 0, credit));
  }
  for (const slug of direct) out.set(slug, 1);
  return [...out];
};

/** One muscle's hard-set credit for the exercise, 0 when it does not train it.
 *  A legacy key ('chest') reads its catalog slug. */
export const hardSetCreditFor = (
  ex: AggregatorExerciseRow,
  muscle: string,
): number => {
  const slug = toCatalogSlug(muscle);
  return resolveExerciseMuscleCredits(ex).find(([m]) => m === slug)?.[1] ?? 0;
};

// Bridge for the paint-map layer ONLY: catalog volume → bodymap volume
// for `volumeBySlugToHeatmap` consumption. The bodymap component reads
// the 20-slug bodymap vocabulary (SVG region IDs); this is the LAST-
// MILE conversion that translates aggregated catalog volume back into
// a paint-able shape. Volume math itself never touches bodymap slugs.
//
// Distribution: catalog "core" maps to bodymap {abs, obliques, lower-
// back} so we split the catalog volume evenly across the bodymap
// regions the catalog slug claims. The previous double-count bug
// (sessionResult.ts:241-249) was the reverse: distributing across
// canonical AND keeping primary @ full, secondary @ half. Here we
// distribute exactly once, evenly, preserving total volume.
export const catalogVolumeToBodymap = (
  catalogVolume: VolumeByMuscleMap,
): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const [slug, v] of Object.entries(catalogVolume)) {
    if (v == null || v <= 0) continue;
    const targets = mapCatalogToCanonical(slug);
    if (targets.length === 0) continue;
    const share = v / targets.length;
    for (const t of targets) {
      out[t] = (out[t] ?? 0) + share;
    }
  }
  return out;
};
